import React, { useState, useCallback, useEffect } from 'react';
import { Handle, Position, type NodeProps, useUpdateNodeInternals } from '@xyflow/react';
import { Button } from '../../ui/Button';
import { Spinner } from '../../ui/Spinner';
import { ImageLightbox } from '../ImageLightbox';
import { VideoPlayer } from './VideoPlayer';
import { useNodeResize } from './useNodeResize';
import { Copy, X, Zap, RefreshCw } from 'lucide-react';

interface VariationNodeDataProps {
  variationPrompt?: string;
  status?: 'idle' | 'generating' | 'complete' | 'error';
  imageUrl?: string;
  videoUrl?: string;
  thumbnailUrl?: string;
  textContent?: string;
  error?: string;
  mediaType?: 'image' | 'video' | 'text';
  nodeWidth?: number;
  onUpdate?: (id: string, updates: any) => void;
  onDelete?: (id: string) => void;
  onWidthChange?: (id: string, width: number) => void;
  onGenerateVariation?: (id: string) => void;
}

export function VariationNode({ id, data: rawData, selected }: NodeProps) {
  const data = rawData as VariationNodeDataProps;
  const [variationPrompt, setVariationPrompt] = useState(data.variationPrompt || '');
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const updateNodeInternals = useUpdateNodeInternals();

  const { nodeWidth, resizeHandleProps } = useNodeResize({
    width: data.nodeWidth,
    minWidth: 200,
    maxWidth: 600,
    onWidthChange: (w) => data.onWidthChange?.(id, w),
  });

  const status = data.status || 'idle';
  const isGenerating = status === 'generating';
  const mediaType = data.mediaType || 'image';
  const isVideo = mediaType === 'video';
  const isText = mediaType === 'text';
  const hasOutput = status === 'complete' && (data.imageUrl || data.videoUrl || data.textContent);

  // Update node internals when content changes
  useEffect(() => {
    const raf = requestAnimationFrame(() => {
      setTimeout(() => updateNodeInternals(id), 50);
    });
    return () => cancelAnimationFrame(raf);
  }, [id, status, data.imageUrl, data.videoUrl, data.textContent, updateNodeInternals]);

  // Save variation prompt on blur
  const handlePromptBlur = useCallback(() => {
    if (data.onUpdate) {
      data.onUpdate(id, { data: { ...data, variationPrompt } });
    }
  }, [id, variationPrompt, data]);

  // Trigger generation
  const handleGenerate = useCallback(() => {
    if (data.onGenerateVariation && !isGenerating) {
      // Save prompt first, then generate
      if (data.onUpdate) {
        data.onUpdate(id, { data: { ...data, variationPrompt } });
      }
      data.onGenerateVariation(id);
    }
  }, [id, data, variationPrompt, isGenerating]);

  // Handle text content edits (for text output)
  const handleTextContentChange = useCallback((newText: string) => {
    if (data.onUpdate) {
      data.onUpdate(id, { data: { ...data, textContent: newText } });
    }
  }, [id, data]);

  // Colors — slightly different from generate node to distinguish
  const headerBg = isText ? 'bg-teal-50/80 border-teal-100' : isVideo ? 'bg-purple-50/80 border-purple-100' : 'bg-gray-50 border-gray-100';
  const headerText = isText ? 'text-teal-700' : isVideo ? 'text-purple-700' : 'text-gray-700';
  const headerIcon = isText ? 'text-teal-600' : isVideo ? 'text-purple-600' : 'text-gray-500';
  const handleColor = isText ? '!bg-teal-400' : isVideo ? '!bg-purple-400' : '!bg-gray-400';

  return (
    <div
      className={`bg-white rounded-lg border-2 shadow-md min-w-[200px] relative ${!nodeWidth ? 'max-w-[280px]' : ''} ${
        selected ? 'border-primary-500' : status === 'error' ? 'border-error-300' : 'border-gray-200'
      }`}
      style={nodeWidth ? { width: nodeWidth } : undefined}
    >
      {/* Header */}
      <div className={`px-3 py-2 border-b rounded-t-lg flex items-center gap-2 ${status === 'error' ? 'bg-error-50 border-error-100' : headerBg}`}>
        {/* Variation icon — branching/split */}
        <Copy className={`w-4 h-4 ${status === 'error' ? 'text-error-600' : headerIcon} ${isGenerating ? 'animate-pulse' : ''}`} />
        <span className={`text-xs font-semibold ${status === 'error' ? 'text-error-700' : headerText}`}>
          Variation
        </span>
        {selected && (
          <button
            onClick={(e) => { e.stopPropagation(); data.onDelete?.(id); }}
            className="ml-auto text-gray-400 hover:text-red-500 p-0.5"
            aria-label="Delete variation"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      <div className="p-3">
        {/* Variation prompt — always visible */}
        <div className="mb-2">
          <textarea
            className="w-full min-h-[48px] text-sm border border-gray-200 rounded-md p-2 resize-y focus:outline-none focus:ring-1 focus:ring-primary-300 focus:border-primary-400 nodrag"
            value={variationPrompt}
            onChange={(e) => setVariationPrompt(e.target.value)}
            onBlur={handlePromptBlur}
            placeholder="Describe the variation (e.g. 'make background darker', 'add sunset sky')..."
          />
        </div>

        {/* Output display */}
        {hasOutput ? (
          <div>
            {data.textContent ? (
              <div className="mb-2">
                <textarea
                  className="w-full min-h-[80px] max-h-[200px] text-sm p-2 border border-gray-200 rounded-md resize-y focus:outline-none focus:ring-1 focus:ring-primary-300 focus:border-primary-400 nodrag"
                  value={data.textContent}
                  onChange={(e) => handleTextContentChange(e.target.value)}
                  placeholder="Generated text..."
                />
              </div>
            ) : data.videoUrl ? (
              <VideoPlayer
                videoUrl={data.videoUrl}
                thumbnailUrl={data.thumbnailUrl}
                className="w-full h-[140px] rounded-md mb-2"
                compact
              />
            ) : data.imageUrl ? (
              <img
                src={data.imageUrl}
                alt="Variation result"
                className="w-full h-auto rounded-md mb-2 cursor-pointer hover:opacity-90 transition-opacity"
                onClick={() => setLightboxOpen(true)}
              />
            ) : null}
            <Button
              variant="outline"
              size="xs"
              fullWidth
              onClick={handleGenerate}
            >
              <RefreshCw className="w-3 h-3" /> Regenerate
            </Button>
          </div>
        ) : status === 'generating' ? (
          <div className="flex flex-col items-center justify-center h-[80px]">
            <Spinner size="md" className="mb-2" />
            <span className="text-xs text-gray-500">
              {isText ? 'Generating text...' : isVideo ? 'Generating video...' : 'Generating...'}
            </span>
          </div>
        ) : status === 'error' ? (
          <div className="text-center">
            <p className="text-xs text-error-600 mb-2">{data.error || 'Generation failed'}</p>
            <Button variant="danger" size="xs" fullWidth onClick={handleGenerate}>
              Retry
            </Button>
          </div>
        ) : (
          <Button
            variant="primary"
            size="xs"
            fullWidth
            onClick={handleGenerate}
            leftIcon={<Zap className="w-3.5 h-3.5" />}
          >
            Generate Variation
          </Button>
        )}
      </div>

      {/* Only target handle (from parent generate node) — no source-left needed */}
      <Handle type="target" position={Position.Left} id="variation-target" className={`!w-3 !h-3 ${handleColor}`} />
      <Handle type="source" position={Position.Right} id="variation-source" className={`!w-3 !h-3 ${handleColor}`} />
      <div {...resizeHandleProps} />
      {lightboxOpen && data.imageUrl && (
        <ImageLightbox src={data.imageUrl} alt="Variation result" onClose={() => setLightboxOpen(false)} />
      )}
    </div>
  );
}
